/**
 * Espejo del motor de cálculo del servidor
 * (`logistic-back/src/shared/pricing/charge-calculator.ts`).
 *
 * Existe SOLO para dar feedback instantáneo mientras el operador
 * escribe. Lo que se guarda y se imprime siempre lo recalcula el
 * backend: si alguna vez los dos difieren, manda el backend.
 *
 * Gemelo de `lclCotizacionCalc.js`. Lo propio del aéreo: la base
 * "Por Peso Cobrable" es el mayor entre el peso bruto y el volumétrico
 * (1:6000), igual que en el servidor.
 */

export const GRUPOS = [
  'FLETE INTERNACIONAL',
  'GASTOS EN ORIGEN',
  'SEGURO',
  'GASTOS EN DESTINO',
  'TRANSPORTE TERRESTRE OTM',
  'DOCUMENTACIÓN',
  'ADUANA',
  'OTROS',
];

export const ORDEN_GRUPO = GRUPOS.reduce((acc, g, i) => {
  acc[g] = (i + 1) * 10;
  return acc;
}, {});

export const BASES_COBRO = [
  'Fijo',
  'Por KG',
  'Por Peso Cobrable',
  'Por Pieza',
  'Por M3',
  'Por Ton',
  'Por W/M',
  'Por BL',
  'Por Contenedor',
  'Por Documento',
  'Por Día',
  '% Valor CIF',
  '% Valor FOB',
  '% del Flete',
  'Porciento',
  'Otro',
];

/** Bases que aplican al aéreo (`BASES_POR_MODO.aereo` del backend). */
export const BASES_AEREO = [
  'Fijo',
  'Por Peso Cobrable',
  'Por KG',
  'Por Pieza',
  'Por Documento',
  'Por Día',
  '% Valor CIF',
  '% Valor FOB',
  '% del Flete',
  'Porciento',
  'Otro',
];

const BASES_PORCENTUALES = ['% Valor CIF', '% Valor FOB', '% del Flete', 'Porciento'];
const BASES_SIN_MINIMO = ['Fijo', 'Por BL', 'Por Documento', 'Otro'];

export const UNIDAD_DE_BASE = {
  'Por KG': 'kg',
  'Por Peso Cobrable': 'kg',
  'Por Pieza': 'bultos',
  'Por M3': 'm³',
  'Por Ton': 'ton',
  'Por W/M': 'W/M',
  'Por BL': 'BL',
  'Por Contenedor': 'cont.',
  'Por Documento': 'doc.',
  'Por Día': 'días',
};

export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

const num = (v) =>
  v === null || v === undefined || v === '' || Number.isNaN(Number(v))
    ? null
    : Number(v);

/** Cantidad que corresponde a cada base. `null` = falta el dato. */
export function cantidadParaBase(base, m) {
  switch (base) {
    case 'Fijo':
    case 'Otro':
      return 1;
    case 'Por BL':
      return num(m.bls) ?? 1;
    case 'Por Documento':
      return num(m.documentos) ?? 1;
    case 'Por Contenedor':
      return num(m.contenedores) ?? 1;
    case 'Por KG':
      return num(m.pesoBrutoKg);
    case 'Por Peso Cobrable': {
      // El mayor entre el real y el volumétrico, como el servidor.
      const explicito = num(m.pesoCobrableKg);
      if (explicito !== null) return explicito;
      if (m.pesoBrutoKg == null && m.pesoVolumetricoKg == null) return null;
      return Math.max(num(m.pesoBrutoKg) ?? 0, num(m.pesoVolumetricoKg) ?? 0);
    }
    case 'Por Pieza':
      return num(m.piezas);
    case 'Por M3':
      return num(m.volumenM3);
    case 'Por Ton': {
      const t = num(m.toneladas);
      if (t !== null) return t;
      const kg = num(m.pesoBrutoKg);
      return kg === null ? null : kg / 1000;
    }
    case 'Por W/M': {
      const wm = num(m.unidadesWM);
      if (wm !== null) return wm;
      if (m.volumenM3 == null && m.toneladas == null) return null;
      return Math.max(1, num(m.volumenM3) ?? 0, num(m.toneladas) ?? 0);
    }
    case 'Por Día':
      return num(m.dias);
    default:
      return null;
  }
}

/**
 * Calcula todas las líneas en dos pasadas.
 *
 * El seguro se cobra como % del CIF y el CIF incluye el flete: si el
 * seguro entrara en su propia base el cálculo sería circular. Por eso
 * primero se cierran las líneas duras, con eso se arma el flete y el
 * CIF, y recién ahí se resuelven los porcentajes.
 */
export function calcularConceptosAereo(conceptos, magnitudes, opciones = {}) {
  const base = (opciones.moneda || 'USD').toUpperCase();
  const secundaria = opciones.monedaSecundaria
    ? String(opciones.monedaSecundaria).toUpperCase()
    : null;
  const trm = Number(opciones.trm) > 0 ? Number(opciones.trm) : null;

  const convertible = (m) => {
    const c = (m || base).toUpperCase();
    return c === base || (!!secundaria && c === secundaria && !!trm);
  };
  const aBase = (valor, m) => {
    const c = (m || base).toUpperCase();
    if (c === base) return valor;
    if (secundaria && c === secundaria && trm) return valor / trm;
    return valor;
  };

  const esPorcentual = (b) => BASES_PORCENTUALES.includes(b);
  const resultados = conceptos.map(() => ({
    cantidad: null,
    total: null,
    totalBase: null,
    aplicoMinimo: false,
    faltaMagnitud: false,
    sinTasa: false,
  }));

  const resolver = (c, i, basePorcentaje) => {
    const sinTarifa = c.tarifaUnitaria === null || c.tarifaUnitaria === undefined || c.tarifaUnitaria === '';
    const baseCobro = sinTarifa ? 'Otro' : c.baseCobro;
    const tarifa = num(c.tarifaUnitaria);
    const minima = num(c.tarifaMinima);

    if (baseCobro === 'Otro') {
      const t = num(c.total) ?? 0;
      resultados[i] = {
        cantidad: 1,
        total: round2(t),
        totalBase: round2(aBase(t, c.moneda)),
        aplicoMinimo: false,
        faltaMagnitud: false,
        sinTasa: !convertible(c.moneda),
      };
      return;
    }

    const cant =
      basePorcentaje !== null
        ? basePorcentaje
        : (num(c.cantidad) ?? cantidadParaBase(baseCobro, magnitudes));

    if (cant === null || tarifa === null) {
      resultados[i] = {
        cantidad: cant,
        total: null,
        totalBase: null,
        aplicoMinimo: false,
        faltaMagnitud: cant === null,
        sinTasa: false,
      };
      return;
    }

    const bruto = basePorcentaje !== null ? (cant * tarifa) / 100 : cant * tarifa;
    let total = round2(bruto);
    let aplicoMinimo = false;
    if (!BASES_SIN_MINIMO.includes(baseCobro) && minima != null && minima > total) {
      total = round2(minima);
      aplicoMinimo = true;
    }
    resultados[i] = {
      cantidad: basePorcentaje !== null ? tarifa : cant,
      total,
      totalBase: round2(aBase(total, c.moneda)),
      aplicoMinimo,
      faltaMagnitud: false,
      sinTasa: !convertible(c.moneda),
    };
  };

  // Pasada A: lo que no depende de otras líneas.
  conceptos.forEach((c, i) => {
    if (!esPorcentual(c.baseCobro)) resolver(c, i, null);
  });

  const fleteBase = round2(
    conceptos.reduce((acc, c, i) => {
      const esFlete = (c.grupo || '').toUpperCase() === 'FLETE INTERNACIONAL';
      const t = resultados[i].totalBase;
      return esFlete && t != null && !resultados[i].sinTasa ? acc + t : acc;
    }, 0),
  );
  const valorFOB = num(magnitudes.valorMercancia) ?? 0;
  const valorCIF = round2(valorFOB + fleteBase);

  // Pasada B: porcentajes, ya con el flete y el CIF cerrados.
  conceptos.forEach((c, i) => {
    if (!esPorcentual(c.baseCobro)) return;
    const b =
      c.baseCobro === '% Valor CIF'
        ? valorCIF
        : c.baseCobro === '% Valor FOB'
          ? valorFOB
          : c.baseCobro === '% del Flete'
            ? fleteBase
            : (num(c.cantidad) ?? 0);
    resolver(c, i, b);
  });

  // Subtotales: una línea sin tasa de cambio NO suma.
  const subtotalPorGrupo = {};
  let subtotal = 0;
  const monedasSinTasa = new Set();
  conceptos.forEach((c, i) => {
    const r = resultados[i];
    if (r.sinTasa) {
      monedasSinTasa.add((c.moneda || base).toUpperCase());
      return;
    }
    if (r.totalBase == null) return;
    const g = c.grupo || 'OTROS';
    subtotalPorGrupo[g] = round2((subtotalPorGrupo[g] || 0) + r.totalBase);
    subtotal = round2(subtotal + r.totalBase);
  });

  return {
    lineas: resultados,
    fleteBase,
    valorFOB,
    valorCIF,
    subtotalPorGrupo,
    subtotal,
    monedasSinTasa: [...monedasSinTasa],
    totalIncompleto: monedasSinTasa.size > 0,
  };
}

/** Formato de importe, estable y sin depender del locale del navegador. */
export const formatImporte = (n, moneda) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  const v = Number(n).toLocaleString('es-CO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return moneda ? `${moneda} ${v}` : v;
};
